import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { BehaviorSubject, EMPTY, Observable } from 'rxjs';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Injectable, OnDestroy } from '@angular/core';
import { ComponentType } from '@angular/cdk/portal';
import { catchError, map } from 'rxjs/operators';
import {
  ADDR_DIALOG_OPTIONS_FULLSCREEN,
  AddressDialogCloseEvent,
  ADDR_DIALOG_OPTIONS,
  AddressCreateDto,
  AppServiceEvent,
  UserService,
  Address,
} from 'src/app/core';
import { ResponsiveService } from '../../services/responsive.service';
import { HttpErrorResponse } from '@angular/common/http';
import { AddressAddComponent } from '../../components';
import { SubSink } from 'subsink';

export interface AddressServiceEvent
  extends AppServiceEvent<
    'create' | 'update' | 'delete' | 'read' | 'init',
    Address
  > {}

@Injectable({ providedIn: 'root' })
export class AddressService implements OnDestroy {
  private statusSubject$: BehaviorSubject<AddressServiceEvent> =
    new BehaviorSubject(this.generateEventObject('init'));

  public get status$(): Observable<AddressServiceEvent> {
    return this.statusSubject$.pipe(catchError(() => EMPTY));
  }

  public subsink = new SubSink();

  // Constructor ------------------------------->

  constructor(
    private responsive: ResponsiveService,
    private addressDialog: MatDialog,
    private userService: UserService,
    private snackbar: MatSnackBar
  ) {}

  // Public methods ---------------------------->

  getUserAddresses(): Observable<Address[]> {
    return this.userService.getUserAddresses();
  }

  addAddress(dto: AddressCreateDto) {
    this.userService.createUserAddress(dto).subscribe((data) => {
      this.statusSubject$.next({ name: 'create', payload: data });
    });
  }

  editAddress(dto: AddressCreateDto) {
    this.userService.updateUserAddress(dto).subscribe((data) => {
      this.statusSubject$.next({ name: 'update', payload: data });
    });
  }

  deleteAddress(id: number): Observable<Address> {
    return this.userService.deleteUserAddress(id).pipe(
      map((data) => {
        this.statusSubject$.next({ name: 'delete', payload: data });
        return data;
      })
    );
  }

  openAddressAddDialogBox(address?: Address) {
    this.openDialog(AddressAddComponent, true);
  }

  openAddressEditDialogBox(address?: Address) {
    this.openDialog(AddressAddComponent, false, address);
  }

  // private methods --------------------------->

  private openDialog<CT>(
    component: ComponentType<CT>,
    isNew = true,
    address?: Address
  ) {
    let reference: MatDialogRef<any, any>;
    let options = ADDR_DIALOG_OPTIONS;

    this.responsive.isHandset$.subscribe((result) => {
      if (reference) return;

      if (result) {
        options = ADDR_DIALOG_OPTIONS_FULLSCREEN;
      }

      options.data = { isNew: isNew };

      if (!isNew) {
        options.data = { model: address };
      }

      reference = this.addressDialog.open(component, options);
      reference.keydownEvents().subscribe((keyEvent) => {
        if (keyEvent.key === 'Escape') reference.close();
      });

      this.subsink.sink = reference
        .afterClosed()
        .subscribe((event: AddressDialogCloseEvent) => {
          this.onClose(event);
        });
    });
  }

  private onClose(event: AddressDialogCloseEvent) {
    if (!event) return;

    switch (event.name) {
      case 'address-add':
        this.addAddress(event.data);
        break;
      case 'address-update':
        this.editAddress(event.data);
        break;

      default:
        break;
    }
  }

  private handleError(err: unknown) {
    if (err instanceof HttpErrorResponse) {
      if (err.status === 404) {
        this.snackbar.open('مشکل در پردازش مقادیر', '', { duration: 1000 });
      }

      if (err.status === 500) {
        this.snackbar.open(
          'مشکل در ذخیره سازی آدرس لطفا در صورت تداوم مشکل با پشتیبانی تماس بگیرید.'
        );
      }
    }
  }

  private generateEventObject(
    name: AddressServiceEvent['name'],
    payload?: AddressServiceEvent['payload']
  ): AddressServiceEvent {
    return { name, payload };
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }
}
